import { Columns, Settings2, X, RotateCcw, CheckCheck } from 'lucide-react'
import type React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/utils'

export interface ColumnConfig {
    id: string
    label: string
    visible: boolean
    group?: string
}

interface Props {
    columns: ColumnConfig[]
    onChange: (id: string) => void
    onSelectAll?: () => void
    onReset?: () => void
    buttonClassName?: string
    buttonText?: string
    compact?: boolean
    title?: string
}

export const ColumnSelector: React.FC<Props> = ({ 
    columns, 
    onChange,
    onSelectAll,
    onReset,
    buttonClassName,
    buttonText,
    compact = false,
    title = 'Display Options'
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const [mounted, setMounted] = useState(false)
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})

    useEffect(() => {
        setMounted(true)
    }, [])

    useEffect(() => {
        const updatePosition = () => {
            if (isOpen && triggerRef.current) {
                const rect = triggerRef.current.getBoundingClientRect()
                const viewportWidth = window.innerWidth
                const viewportHeight = window.innerHeight
                const width = 300 // w-[300px]
                const gap = 8

                // Horizontal Positioning (Right anchored preference)
                let left = rect.right - width
                if (left < 10) left = 10 // Left boundary
                if (left + width > viewportWidth - 10) left = viewportWidth - width - 10 // Right boundary

                // Vertical Positioning (Smart Flip)
                const spaceBelow = viewportHeight - rect.bottom
                const spaceAbove = rect.top
                const dropdownHeightEstimate = 420 // rough max height estimate

                let top: number | undefined
                let bottom: number | undefined
                let maxHeight: number
                let transformOrigin: string

                // Prefer bottom if space allows, or if neither allows fully but bottom has more space
                if (spaceBelow >= dropdownHeightEstimate || spaceBelow >= spaceAbove) {
                    top = rect.bottom + gap
                    maxHeight = Math.min(dropdownHeightEstimate, spaceBelow - 20) // 20px buffer
                    transformOrigin = 'top right'
                } else {
                    // Flip to top
                    bottom = viewportHeight - rect.top + gap
                    maxHeight = Math.min(dropdownHeightEstimate, spaceAbove - 20)
                    transformOrigin = 'bottom right'
                }

                setDropdownStyle({
                    position: 'fixed',
                    left: `${left}px`,
                    top: top !== undefined ? `${top}px` : undefined,
                    bottom: bottom !== undefined ? `${bottom}px` : undefined,
                    maxHeight: `${maxHeight}px`,
                    zIndex: 9999,
                    transformOrigin,
                })
            }
        }

        if (isOpen) {
            updatePosition()
            window.addEventListener('resize', updatePosition)
            window.addEventListener('scroll', updatePosition, true)
        }

        return () => {
            window.removeEventListener('resize', updatePosition)
            window.removeEventListener('scroll', updatePosition, true)
        }
    }, [isOpen])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!isOpen) return

            if (triggerRef.current && !triggerRef.current.contains(event.target as Node)) {
                // Check if click is inside the portal content
                const target = event.target as HTMLElement
                const dropdown = document.querySelector('.column-selector-dropdown')
                if (dropdown && dropdown.contains(target)) {
                    return
                }
                setIsOpen(false)
            }
        }
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen])

    const activeCount = columns.filter((c) => c.visible).length

    // Grouping computation if any column has group defined
    const hasGroups = useMemo(() => columns.some(c => !!c.group), [columns])
    const groupedColumns = useMemo(() => {
        if (!hasGroups) return []
        const map = new Map<string, ColumnConfig[]>()
        for (const col of columns) {
            const grp = col.group || 'Other'
            if (!map.has(grp)) map.set(grp, [])
            map.get(grp)!.push(col)
        }
        return Array.from(map.entries())
    }, [columns, hasGroups])

    const getGroupBadgeClass = (groupName: string) => {
        const lower = groupName.toLowerCase()
        if (lower.includes('call') || lower === 'ce') return 'text-cyan-600 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-950/40 border-cyan-200 dark:border-cyan-800'
        if (lower.includes('put') || lower === 'pe') return 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-950/40 border-purple-200 dark:border-purple-800'
        return 'text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700'
    }

    const renderColumnRow = (col: ColumnConfig) => {
        const isCe = col.group?.toLowerCase().includes('ce') || col.id.toLowerCase().startsWith('ce')
        const isPe = col.group?.toLowerCase().includes('pe') || col.id.toLowerCase().startsWith('pe')

        return (
            <label
                key={col.id}
                className="flex items-center justify-between px-3 py-2 rounded-xl hover:bg-slate-50/80 dark:hover:bg-slate-800/80 cursor-pointer group transition-all duration-200 select-none active:scale-[0.98]"
            >
                <div className="flex items-center gap-2.5 overflow-hidden">
                    <div className={cn(
                        "p-1.5 rounded-lg shadow-sm group-hover:scale-105 transition-transform duration-200",
                        isCe 
                            ? "bg-cyan-500 text-white shadow-cyan-500/20"
                            : isPe
                            ? "bg-purple-500 text-white shadow-purple-500/20"
                            : "bg-indigo-500 text-white shadow-indigo-500/20"
                    )}>
                        <Columns size={13} />
                    </div>
                    <span
                        className={cn(
                            'text-xs font-bold truncate transition-colors font-mono',
                            col.visible
                                ? 'text-slate-800 dark:text-slate-200'
                                : 'text-slate-400 dark:text-slate-500',
                        )}
                    >
                        {col.label}
                    </span>
                </div>

                {/* IOS Style Toggle */}
                <div
                    className={cn(
                        'w-8 h-4.5 rounded-full relative transition-colors duration-300 shadow-inner shrink-0',
                        col.visible
                            ? isCe 
                                ? 'bg-cyan-600'
                                : isPe
                                ? 'bg-purple-600'
                                : 'bg-slate-800 dark:bg-emerald-500'
                            : 'bg-slate-200 dark:bg-slate-700',
                    )}
                >
                    <div
                        className={cn(
                            'absolute top-0.5 left-0.5 bg-white h-3.5 w-3.5 rounded-full shadow-xs transition-transform duration-300',
                            col.visible ? 'translate-x-3.5' : 'translate-x-0',
                        )}
                    />
                </div>

                <input
                    type="checkbox"
                    className="hidden"
                    checked={col.visible}
                    onChange={() => onChange(col.id)}
                />
            </label>
        )
    }

    return (
        <>
            {compact ? (
                <button
                    ref={triggerRef}
                    onClick={() => setIsOpen(!isOpen)}
                    title="Configure visible columns"
                    className={buttonClassName || cn(
                        "flex items-center gap-1.5 h-6 px-2.5 rounded-lg text-[10px] font-black transition-all cursor-pointer shadow-2xs",
                        isOpen
                            ? "bg-emerald-600 text-white shadow-xs"
                            : "bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-emerald-500"
                    )}
                >
                    <Settings2
                        size={12}
                        className={cn(
                            "transition-transform duration-300",
                            isOpen ? "rotate-90 text-white" : "text-emerald-500"
                        )}
                    />
                    <span>{buttonText || `COLUMNS (${activeCount}/${columns.length})`}</span>
                </button>
            ) : (
                <button
                    ref={triggerRef}
                    onClick={() => setIsOpen(!isOpen)}
                    className={buttonClassName || cn(
                        'group flex items-center gap-2.5 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-300 active:scale-95 border cursor-pointer',
                        isOpen
                            ? 'bg-slate-800 dark:bg-slate-700 border-slate-700 dark:border-slate-600 text-white shadow-lg shadow-slate-900/20 ring-2 ring-slate-200 dark:ring-slate-600'
                            : 'bg-white/40 dark:bg-slate-800/40 backdrop-blur-md border-white/40 dark:border-slate-700/40 text-slate-600 dark:text-slate-300 hover:border-indigo-200 dark:hover:border-slate-600 hover:text-slate-800 dark:hover:text-slate-100 dark:shadow-slate-900/10 hover:shadow-md ring-1 ring-white/40 dark:ring-slate-700/40',
                    )}
                >
                    <Settings2
                        size={16}
                        className={cn(
                            'transition-transform duration-500',
                            isOpen ? 'rotate-180 text-emerald-400' : 'group-hover:rotate-90',
                        )}
                    />
                    <span>{buttonText || 'Columns'}</span>
                    {activeCount < columns.length && (
                        <span
                            className={cn(
                                'flex items-center justify-center text-[9px] h-5 w-5 rounded-full ml-1 font-black transition-colors',
                                isOpen
                                    ? 'bg-emerald-500 text-white'
                                    : 'bg-slate-100 text-slate-600 group-hover:bg-slate-200',
                            )}
                        >
                            {activeCount}
                        </span>
                    )}
                </button>
            )}

            {isOpen &&
                mounted &&
                createPortal(
                    <div
                        className="column-selector-dropdown fixed w-[300px] bg-white/95 dark:bg-slate-900/95 backdrop-blur-2xl rounded-2xl shadow-2xl shadow-slate-900/20 border border-slate-200/80 dark:border-slate-700/80 overflow-hidden animate-in fade-in zoom-in-95 duration-200 ring-1 ring-slate-200/60 dark:ring-slate-700/60 flex flex-col z-[9999]"
                        style={dropdownStyle}
                    >
                        <div className="px-4 py-3 bg-gradient-to-r from-slate-50/90 to-white/90 dark:from-slate-800/90 dark:to-slate-900/90 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center shrink-0">
                            <div>
                                <h4 className="text-xs font-black text-slate-800 dark:text-slate-200 uppercase tracking-widest flex items-center gap-1.5">
                                    <Settings2 size={13} className="text-emerald-500" />
                                    <span>{title}</span>
                                </h4>
                                <div className="flex items-center gap-2 mt-1">
                                    <p className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">
                                        Select columns to display
                                    </p>
                                    {(onSelectAll || onReset) && (
                                        <div className="flex items-center gap-1.5 ml-2">
                                            {onSelectAll && (
                                                <button
                                                    type="button"
                                                    onClick={onSelectAll}
                                                    className="inline-flex items-center gap-0.5 text-[9.5px] font-bold text-emerald-600 dark:text-emerald-400 hover:underline cursor-pointer"
                                                >
                                                    <CheckCheck size={10} /> All
                                                </button>
                                            )}
                                            {onSelectAll && onReset && <span className="text-slate-300 dark:text-slate-600 text-[10px]">|</span>}
                                            {onReset && (
                                                <button
                                                    type="button"
                                                    onClick={onReset}
                                                    className="inline-flex items-center gap-0.5 text-[9.5px] font-bold text-slate-500 dark:text-slate-400 hover:underline cursor-pointer"
                                                >
                                                    <RotateCcw size={9} /> Reset
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => setIsOpen(false)}
                                className="h-6 w-6 flex items-center justify-center rounded-full hover:bg-rose-50 dark:hover:bg-rose-900/20 text-slate-400 hover:text-rose-500 transition-all duration-200 cursor-pointer"
                            >
                                <X size={14} strokeWidth={2.5} />
                            </button>
                        </div>

                        <div className="py-2 overflow-y-auto custom-scrollbar px-2 space-y-1 max-h-[60vh] sm:max-h-[380px]">
                            {hasGroups ? (
                                groupedColumns.map(([grpName, grpCols]) => (
                                    <div key={grpName} className="mb-2 last:mb-0">
                                        <div className="px-2 pt-1 pb-1">
                                            <span className={cn(
                                                "inline-flex items-center px-2 py-0.5 rounded-md text-[8.5px] font-black uppercase tracking-wider border",
                                                getGroupBadgeClass(grpName)
                                            )}>
                                                {grpName}
                                            </span>
                                        </div>
                                        <div className="space-y-0.5 mt-0.5">
                                            {grpCols.map(renderColumnRow)}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                columns.map(renderColumnRow)
                            )}
                        </div>

                        <div className="px-4 py-2.5 bg-slate-50/80 dark:bg-slate-800/60 border-t border-slate-100 dark:border-slate-800 shrink-0 flex justify-between items-center text-[10px] font-semibold text-slate-400 dark:text-slate-500 font-mono">
                            <span>
                                Visible: <span className="text-slate-800 dark:text-slate-200 font-black">{activeCount}</span>
                            </span>
                            <span>Total: <span className="font-black">{columns.length}</span></span>
                        </div>
                    </div>,
                    document.body,
                )}
        </>
    )
}
